const API_BASE_URL = import.meta.env.VITE_API_URL || "";
const IS_MOCK = !API_BASE_URL;

// Mock data for local development without a backend
const MOCK_DATA: Record<string, unknown> = {
  "GET:/api/scenarios": {
    items: [
      { scenarioId: "s1", orgId: "mock-org", name: "Cold Call: SaaS Pitch", description: "Practice cold calling a VP of Sales at a mid-market SaaS company. Handle initial resistance and book a meeting.", type: "cold_call", difficulty: "beginner", personas: ["skeptical", "executive"], isActive: true, goal: "Book a 30-minute discovery meeting", successCriteria: ["Open with a relevant hook", "Identify a pain point", "Propose a specific next step", "Handle at least one objection"], context: "", createdAt: "2026-04-01" },
      { scenarioId: "s2", orgId: "mock-org", name: "Discovery: Pain Points", description: "Run a discovery call to uncover business pain points. Ask open-ended questions and qualify the opportunity.", type: "discovery", difficulty: "intermediate", personas: ["technical", "executive"], isActive: true, goal: "Uncover 3 business pain points and qualify the opportunity", successCriteria: ["Ask open-ended questions", "Dig deeper on initial answers", "Quantify the impact of pain points", "Confirm budget and timeline"], context: "", createdAt: "2026-04-01" },
      { scenarioId: "s3", orgId: "mock-org", name: "Objection: Price Too High", description: "Handle the classic 'your price is too high' objection. Reframe value and negotiate without discounting.", type: "objection_handling", difficulty: "advanced", personas: ["skeptical", "technical", "executive"], isActive: true, goal: "Overcome the price objection without discounting", successCriteria: ["Acknowledge the concern", "Reframe around value and ROI", "Use a specific customer example", "Get agreement to move forward"], context: "", createdAt: "2026-04-01" },
    ],
    count: 3,
  },
  "GET:/api/sessions": {
    items: [
      { sessionId: "sess1", orgId: "mock-org", userId: "mock-user-001", scenarioId: "s1", personaType: "skeptical", mode: "text", status: "completed", transcript: [{ role: "rep", message: "Hi, this is Alex from Nimbus. Do you have a moment?", timestamp: "2026-04-08T10:00:00Z" }, { role: "prospect", message: "I'm pretty busy. What's this about?", timestamp: "2026-04-08T10:00:05Z" }, { role: "rep", message: "I understand. I'll be brief — we help sales teams cut ramp time by 40% with AI coaching.", timestamp: "2026-04-08T10:00:15Z" }, { role: "prospect", message: "We already have a training program. What makes yours different?", timestamp: "2026-04-08T10:00:20Z" }], score: { overallScore: 72, kpiScores: [{ kpiName: "tone", score: 80, weight: 0.25, feedback: "Good warmth" }, { kpiName: "clarity", score: 75, weight: 0.25, feedback: "Clear but could be more concise" }], redFlagsDetected: [], strengthAreas: ["Opening", "Tone"], improvementAreas: [{ area: "Value Proposition", description: "Could articulate ROI more specifically", examples: ["Missing concrete numbers"], priority: "high" }], scoredAt: "2026-04-08T10:05:00Z" }, durationMs: 300000, startedAt: "2026-04-08T10:00:00Z", endedAt: "2026-04-08T10:05:00Z", createdAt: "2026-04-08T10:00:00Z" },
      { sessionId: "sess2", orgId: "mock-org", userId: "mock-user-001", scenarioId: "s2", personaType: "technical", mode: "text", status: "completed", transcript: [], score: { overallScore: 85, kpiScores: [], redFlagsDetected: [], strengthAreas: ["Discovery questions"], improvementAreas: [{ area: "Follow-up", description: "Dig deeper on initial answers", examples: [], priority: "medium" }], scoredAt: "2026-04-07T14:00:00Z" }, durationMs: 420000, startedAt: "2026-04-07T14:00:00Z", endedAt: "2026-04-07T14:07:00Z", createdAt: "2026-04-07T14:00:00Z" },
    ],
    count: 2,
  },
  "GET:/api/coaching/plan": {
    planId: "mock-org#mock-user-001", orgId: "mock-org", userId: "mock-user-001", strengthScore: 76,
    scoreTrend: [{ date: "2026-04-05", score: 65, sessionId: "s1" }, { date: "2026-04-07", score: 72, sessionId: "s2" }, { date: "2026-04-08", score: 85, sessionId: "s3" }],
    improvementAreas: [
      { area: "Value Articulation", currentScore: 68, targetScore: 85, progress: 45, tips: ["Lead with customer outcomes, not features", "Use specific ROI numbers from case studies"] },
      { area: "Objection Handling", currentScore: 60, targetScore: 80, progress: 30, tips: ["Acknowledge the objection before responding", "Use the 'feel, felt, found' framework"] },
      { area: "Discovery Questions", currentScore: 82, targetScore: 90, progress: 70, tips: ["Ask more open-ended follow-ups", "Dig into the 'why' behind stated pain points"] },
    ],
    recommendedScenarios: [
      { scenarioId: "s3", scenarioName: "Objection: Price Too High", reason: "Your objection handling score is lowest — practice reframing value" },
      { scenarioId: "s1", scenarioName: "Cold Call: SaaS Pitch", reason: "Strengthen your opening value proposition" },
    ],
    sessionsAnalyzed: 3, generatedAt: "2026-04-08T12:00:00Z",
  },
  "GET:/api/content": {
    items: [
      { contentId: "c1", orgId: "mock-org", title: "Competitor Battlecard: Acme Corp", description: "Key differentiators vs Acme Corp. Focus on our AI coaching advantage and faster ramp times. They lack real-time feedback.", type: "ai_generated", tags: ["competitor"], status: "published", duration: 45, createdBy: "admin", createdAt: "2026-04-01" },
      { contentId: "c2", orgId: "mock-org", title: "Handling 'We Already Have Training'", description: "When prospects say they already have training: 1) Acknowledge their investment, 2) Ask about measurable outcomes, 3) Share our 40% ramp reduction stat.", type: "ai_generated", tags: ["objection"], status: "published", duration: 30, createdBy: "admin", createdAt: "2026-04-02" },
      { contentId: "c3", orgId: "mock-org", title: "Product Demo: AI Role-Play", description: "Quick walkthrough of the AI role-play feature. Show scenario selection, persona types, and real-time feedback.", type: "video", tags: ["product"], status: "published", duration: 60, createdBy: "admin", createdAt: "2026-04-03" },
    ],
    count: 3,
  },
  "GET:/api/users": { items: [
    { userId: "mock-user-001", orgId: "mock-org", email: "admin@nimbus.dev", name: "Dev Admin", role: "admin", status: "active", totalXP: 0, createdAt: "2026-04-01" },
    { userId: "mock-user-002", orgId: "mock-org", email: "sarah@nimbus.dev", name: "Sarah Chen", role: "sales_manager", teamId: "t1", status: "active", totalXP: 0, createdAt: "2026-04-01" },
    { userId: "mock-user-003", orgId: "mock-org", email: "mike@nimbus.dev", name: "Mike Johnson", role: "sales_rep", teamId: "t1", status: "active", totalXP: 0, createdAt: "2026-04-02" },
    { userId: "mock-user-004", orgId: "mock-org", email: "lisa@nimbus.dev", name: "Lisa Park", role: "sales_rep", teamId: "t1", status: "invited", totalXP: 0, createdAt: "2026-04-03" },
  ], count: 4 },
  "GET:/api/teams": { items: [
    { teamId: "t1", orgId: "mock-org", name: "Enterprise Sales", managerId: "mock-user-002", memberCount: 2, createdAt: "2026-04-01" },
  ], count: 1 },
  "GET:/api/scoring-config": {
    configId: "default", orgId: "mock-org",
    kpis: [
      { name: "tone", description: "Tone and empathy", weight: 0.25, evaluationCriteria: "Evaluate warmth and professionalism" },
      { name: "clarity", description: "Message clarity", weight: 0.25, evaluationCriteria: "Evaluate how clearly points are communicated" },
      { name: "methodology_adherence", description: "Sales methodology adherence", weight: 0.30, evaluationCriteria: "Evaluate adherence to sales framework" },
      { name: "red_flag_avoidance", description: "Avoiding red flag phrases", weight: 0.20, evaluationCriteria: "Check for prohibited phrases" },
    ],
    redFlagPhrases: ["to be honest", "trust me", "I think", "basically"],
    methodology: "Nimbus Value Selling Framework",
  },
  "GET:/api/coaching/team/t1": {
    teamId: "t1",
    members: [
      { userId: "mock-user-003", sessionCount: 5, avgScore: 78, lastActive: "2026-04-08T10:00:00Z", hasCoachingPlan: true },
      { userId: "mock-user-004", sessionCount: 0, avgScore: null, lastActive: null, hasCoachingPlan: false },
    ],
    totalSessions: 5, averageScore: 78,
  },
};

function getMockResponse(method: string, path: string, body?: unknown): unknown {
  // Strip query params for lookup
  const cleanPath = path.split("?")[0];
  const key = `${method}:${cleanPath}`;

  if (MOCK_DATA[key]) return MOCK_DATA[key];

  // Handle parameterized routes
  for (const [mockKey, mockValue] of Object.entries(MOCK_DATA)) {
    const pattern = mockKey.replace(/\{[^}]+\}/g, "[^/]+");
    if (new RegExp(`^${pattern}$`).test(key)) return mockValue;
  }

  // Default responses for mutations
  if (method === "POST") {
    // Return a proper session object for session creation
    if (cleanPath === "/api/sessions") {
      const reqBody = body as Record<string, unknown> | undefined;
      return {
        sessionId: "mock-session-" + Date.now(),
        orgId: "mock-org-001",
        userId: "mock-user-001",
        scenarioId: reqBody?.scenarioId || "s1",
        personaType: reqBody?.personaType || "skeptical",
        mode: reqBody?.mode || "text",
        status: "active",
        transcript: [],
        durationMs: 0,
        startedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
      };
    }
    return { id: "mock-" + Date.now(), success: true };
  }
  if (method === "PUT") return { success: true };
  if (method === "DELETE") return { deleted: true };

  return { items: [], count: 0 };
}

async function getToken(): Promise<string> {
  if (IS_MOCK) return "mock-token";

  const { CognitoUserPool } = await import("amazon-cognito-identity-js");
  const pool = new CognitoUserPool({
    UserPoolId: import.meta.env.VITE_USER_POOL_ID,
    ClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
  });
  const user = pool.getCurrentUser();
  if (!user) throw new Error("Not authenticated");

  return new Promise((resolve, reject) => {
    user.getSession((err: Error | null, session: any) => {
      if (err || !session?.isValid()) reject(err || new Error("Invalid session"));
      else resolve(session.getIdToken().getJwtToken());
    });
  });
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  if (IS_MOCK) {
    // Simulate network delay
    await new Promise((r) => setTimeout(r, 300));
    return getMockResponse(method, path, body) as T;
  }

  const token = await getToken();
  const url = `${API_BASE_URL}${path}`;

  const response = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const status = response.status;
    if (status >= 400 && status < 500) {
      console.warn(`API ${method} ${path} returned ${status}`);
    } else {
      console.error(`API ${method} ${path} returned ${status}`);
    }
    const errorBody = await response.json().catch(() => ({ message: "Request failed" }));
    throw new Error(errorBody.message || `HTTP ${status}`);
  }

  return response.json();
}

export const apiGet = <T>(path: string) => request<T>("GET", path);
export const apiPost = <T>(path: string, body?: unknown) => request<T>("POST", path, body);
export const apiPut = <T>(path: string, body?: unknown) => request<T>("PUT", path, body);
export const apiDelete = <T>(path: string) => request<T>("DELETE", path);
